import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'

import { useAuth } from '@/hooks/useAuth'
import {
    applyHashtagAttribution,
    candidateAccountIdsForAttribution,
    chunkSortedIds,
    computeHashtagMatches,
} from '@/hooks/hashtagAttributionScope'
import { useMastodonFollowedTags } from '@/hooks/useMastodonFollowedTags'
import { getBatchRelationships, getMastodonAccountId } from '@/lib/mastodon'
import type { Post } from '@/types/posts'

export {
    applyHashtagAttribution,
    candidateAccountIdsForAttribution,
    chunkSortedIds,
    computeHashtagMatches,
    MAX_BANNER_TAGS,
    splitBannerTags,
} from '@/hooks/hashtagAttributionScope'
export type { AttributionPost } from '@/hooks/hashtagAttributionScope'

export interface UseMastodonHashtagAttributionOptions {
    posts: Post[]
    enabled: boolean
}

export interface UseMastodonHashtagAttributionResult {
    posts: Post[]
    isFetching: boolean
}

const EMPTY_MATCHES: ReadonlyMap<string, string[]> = new Map()

/**
 * Enriches Following-timeline posts with `viaFollowedHashtags` when they
 * surfaced via a hashtag the viewer follows but whose delivering account they
 * do NOT follow.
 *
 * The webapp has no shared, React-Query-backed relationship store to reuse, so
 * this hook owns a dedicated, account-scoped, chunked query cache. Follow/
 * unfollow freshness is supplied by an explicit invalidation in `useFollowUser`
 * (plus a short staleTime as a second line of defence).
 *
 * Never gates the primary render: posts are returned unchanged until a
 * relationship resolves, and posts the enrichment didn't touch keep their
 * original object reference.
 */
export function useMastodonHashtagAttribution({
    posts,
    enabled,
}: UseMastodonHashtagAttributionOptions): UseMastodonHashtagAttributionResult {
    const { mastodonSession } = useAuth()
    const accountScope = mastodonSession ? getMastodonAccountId(mastodonSession) : null
    const viewerAccountId = mastodonSession?.account.id

    const { tags: followedTags } = useMastodonFollowedTags()

    const followedTagNames = useMemo(() => followedTags.map(t => t.name), [followedTags])

    const matches = useMemo(
        () =>
            enabled
                ? computeHashtagMatches(posts, followedTagNames, viewerAccountId)
                : EMPTY_MATCHES,
        [enabled, posts, followedTagNames, viewerAccountId]
    )

    const candidateIds = useMemo(
        () => (enabled ? candidateAccountIdsForAttribution(posts, matches) : []),
        [enabled, posts, matches]
    )
    const candidateIdsKey = candidateIds.join(',')

    const chunks = useMemo(
        () => (enabled ? chunkSortedIds(candidateIds) : []),
        [enabled, candidateIds]
    )

    const relationshipQueries = useQueries({
        queries: chunks.map(chunk => ({
            queryKey: [
                'mastodonHashtagAttribution',
                'relationship',
                accountScope,
                chunk.join(','),
            ] as const,
            queryFn: () => getBatchRelationships(mastodonSession!, chunk),
            enabled: enabled && chunk.length > 0 && !!mastodonSession,
            staleTime: 60_000,
        })),
    })

    const isFetching = relationshipQueries.some(q => q.isFetching)

    // Snapshot resolved data only when it changes, mirroring useMixedTimeline.
    const relationshipDataKey = relationshipQueries
        .map(q => `${q.dataUpdatedAt}:${q.data ? q.data.size : '-'}`)
        .join('|')

    const cacheReader = useMemo(() => {
        const resolved = new Map<string, boolean>()
        relationshipQueries.forEach(query => {
            query.data?.forEach((rel, accountId) => {
                resolved.set(accountId, rel.following)
            })
        })
        return (accountId: string) => resolved.get(accountId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateIdsKey, relationshipDataKey])

    const enrichedPosts = useMemo(
        () => (enabled ? applyHashtagAttribution(posts, matches, cacheReader) : posts),
        [enabled, posts, matches, cacheReader]
    )

    return { posts: enrichedPosts as Post[], isFetching }
}
